import React, { useEffect } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { useCheckout } from '../CheckoutContext';

/**
 * OpenBro V2 landing. Portrait layout from the approved poster:
 * space background, four promises, 49€ card.
 */

const COPY = {
  fr: {
    lang: 'FR',
    eyebrow: 'DISCIPLINE · ACTION · RÉSULTATS',
    title: 'PRENDS\nLE CONTRÔLE\nDE TA VIE.',
    lead: 'Tu m’as en direct. Je règle n’importe quel problème. Et tu prends les six modules. Pas un. Les six.',
    modsLabel: 'LES SIX',
    modules: [
      ['#FF5A36', 'Musculation', 'Le corps. Zéro excuse.'],
      ['#3DDC72', 'Nutrition', 'Tu manges pour gagner.'],
      ['#3D8BFF', 'E-commerce', 'Tu vends. Pour de vrai.'],
      ['#7A5CFF', 'Montage', 'Tes vidéos arrêtent d’être molles.'],
      ['#F0A202', 'Tournage', 'Tu filmes comme si ça comptait.'],
      ['#21C7B7', 'IA', 'Ça bosse pendant que tu dors.'],
    ],
    members: '+10 000 membres',
    membersSub: 'Entrepreneurs, investisseurs, créateurs, sportifs...',
    price: '49€',
    period: '/ mois',
    cancel: 'Résiliable à tout moment',
    access: 'ACCÈS COMPLET',
    perks: [
      'Gauth en direct. Pas un assistant.',
      'Les six modules. Ouverts.',
      'N’importe quel problème, réglé avec toi.',
      'Un suivi chaque mois. Je vois si tu as bougé.',
    ],
    cta: 'Parler avec Gauth maintenant',
  },
  en: {
    lang: 'EN',
    eyebrow: 'DISCIPLINE · ACTION · RESULTS',
    title: 'TAKE\nCONTROL\nOF YOUR LIFE.',
    lead: 'You get me, direct. I fix any problem. And you take all six modules. Not one. All six.',
    modsLabel: 'ALL SIX',
    modules: [
      ['#FF5A36', 'Training', 'The body. No excuse.'],
      ['#3DDC72', 'Nutrition', 'You eat to win.'],
      ['#3D8BFF', 'E-commerce', 'You sell. For real.'],
      ['#7A5CFF', 'Editing', 'Your videos stop being soft.'],
      ['#F0A202', 'Filming', 'You shoot like it matters.'],
      ['#21C7B7', 'AI', 'It works while you sleep.'],
    ],
    members: '+10,000 members',
    membersSub: 'Founders, investors, creators, athletes...',
    price: '49€',
    period: '/ month',
    cancel: 'Cancel anytime',
    access: 'FULL ACCESS',
    perks: [
      'Gauth, direct. Not an assistant.',
      'All six modules. Open.',
      'Any problem, handled with you.',
      'A check every month. I see if you moved.',
    ],
    cta: 'Talk to Gauth now',
  },
};

function Avatars() {
  return (
    <div className="v2-faces" aria-hidden="true">
      {[0, 25, 50, 75, 100].map((pos) => (
        <span key={pos} style={{ backgroundPosition: `${pos}% center` }} />
      ))}
    </div>
  );
}

export default function OpenBroV2() {
  const { lang, toggleLang } = useLang();
  const { startCheckout, isActive } = useCheckout();
  const t = COPY[lang] || COPY.fr;

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  return (
    <div className="v2-root">
      <main className="v2">
        <header className="v2-top">
          <div className="v2-logo">OpenBro<span>✦</span></div>
          <button type="button" className="v2-lang" onClick={toggleLang}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.8 3.8 5.8 3.8 9S14.5 18.2 12 21c-2.5-2.8-3.8-5.8-3.8-9S9.5 5.8 12 3z" /></svg>
            {t.lang}
          </button>
        </header>

        <p className="v2-eye">{t.eyebrow}</p>
        <h1>{t.title}</h1>
        <p className="v2-lead">{t.lead}</p>
        <p className="v2-mods-label">{t.modsLabel}</p>
        <ul className="v2-mods">
          {t.modules.map(([color, name, line]) => (
            <li key={name} style={{ '--c': color }}>
              <strong>{name}</strong>
              <span>{line}</span>
            </li>
          ))}
        </ul>

        <div className="v2-social">
          <Avatars />
          <div>
            <strong>{t.members}</strong>
            <span>{t.membersSub}</span>
          </div>
        </div>

        <section className="v2-card">
          <div className="v2-price-row">
            <div>
              <b>{t.price}</b>
              <em>{t.period}</em>
            </div>
            <span className="v2-cancel">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M13 3l1.2 3.2L17.5 7 14.2 8.2 13 11.5 11.8 8.2 8.5 7l3.3-.8L13 3zM6 13l.7 1.8L8.5 15.5 6.7 16.2 6 18l-.7-1.8L3.5 15.5l1.8-.7L6 13z" /></svg>
              {t.cancel}
            </span>
          </div>
          <p className="v2-access">{t.access}</p>
          <ul className="v2-perks">
            {t.perks.map((perk) => (
              <li key={perk}><span>✓</span>{perk}</li>
            ))}
          </ul>
          <button type="button" className="v2-cta" disabled={isActive} onClick={() => startCheckout('v2-cta')}>
            {isActive ? '...' : t.cta} <span>→</span>
          </button>
        </section>
      </main>
      <div className="v2-photo" role="img" aria-label="OpenBro" />
      <style>{`
        .v2-root { position: fixed; inset: 0; overflow: hidden; display: grid; grid-template-columns: 1fr; grid-template-rows: 34vh minmax(0, 1fr); background: #000; }
        .v2 { grid-row: 2; min-height: 0; overflow: hidden; color: #f4f7fb; padding: 12px 16px 14px; display: flex; flex-direction: column; background: #000; font-family: Inter, -apple-system, sans-serif; }
        .v2-photo { grid-row: 1; min-height: 0; background: #000 url(/v2-hero.jpg) center center / contain no-repeat; }
        .v2-top { display: flex; align-items: center; justify-content: space-between; }
        .v2-logo { font-family: "Space Grotesk", Inter, sans-serif; font-weight: 700; font-size: 28px; letter-spacing: -0.04em; }
        .v2-logo span { font-size: 14px; margin-left: 2px; }
        .v2-lang { display: flex; align-items: center; gap: 6px; background: rgba(255,255,255,0.06); color: #fff; border: 1px solid rgba(255,255,255,0.14); border-radius: 999px; padding: 6px 10px; font-size: 13px; }
        .v2-lang svg { width: 16px; height: 16px; fill: none; stroke: currentColor; stroke-width: 1.6; }
        .v2-eye { margin: 12px 0 4px; font-size: 11px; letter-spacing: 0.22em; color: rgba(255,255,255,0.55); }
        .v2 h1 { margin: 0; max-width: 12ch; font-family: "Space Grotesk", Inter, sans-serif; font-size: clamp(26px, 3.4vh, 40px); line-height: 0.9; letter-spacing: -0.045em; font-weight: 700; white-space: pre-line; }
        .v2-lead { max-width: 46ch; margin: 6px 0 0; font-size: 13px; line-height: 1.3; color: rgba(255,255,255,0.78); }
        .v2-mods-label { margin: 8px 0 6px; font-size: 10px; letter-spacing: 0.2em; color: rgba(255,255,255,0.4); }
        .v2-mods { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .v2-mods li { border-radius: 14px; padding: 10px 10px 10px 12px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-left: 3px solid var(--c); }
        .v2-mods strong { display: block; font-size: 13px; }
        .v2-mods span { display: block; margin-top: 2px; font-size: 11px; line-height: 1.3; color: rgba(255,255,255,0.62); }
        .v2-social { display: none; }
        .v2-faces { display: flex; }
        .v2-faces span { width: 32px; height: 32px; border-radius: 50%; border: 2px solid #0b0e14; margin-left: -8px; background: url(/v2-avatars.jpg) center / 500% auto no-repeat; }
        .v2-faces span:first-child { margin-left: 0; }
        .v2-social strong { display: block; font-size: 14px; }
        .v2-social span { display: block; max-width: 22ch; font-size: 11px; color: rgba(255,255,255,0.6); }
        .v2-card { margin-top: 8px; padding: 10px 12px 10px; border-radius: 22px; background: #0c0c0c; border: 1px solid rgba(255,255,255,0.14); }
        .v2-price-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
        .v2-price-row b { font-family: "Space Grotesk", Inter, sans-serif; font-size: clamp(36px, 5vh, 52px); letter-spacing: -0.05em; line-height: 0.9; }
        .v2-price-row em { font-style: normal; font-size: 18px; color: rgba(255,255,255,0.7); }
        .v2-cancel { display: flex; align-items: center; gap: 6px; font-size: 12px; color: rgba(255,255,255,0.75); max-width: 11ch; }
        .v2-cancel svg { width: 16px; height: 16px; fill: #fff; flex: none; }
        .v2-access { display: none; }
        .v2-perks { display: none; }
        .v2-perks li { display: flex; gap: 6px; font-size: 12px; line-height: 1.35; color: rgba(255,255,255,0.88); }
        .v2-perks span { color: #fff; }
        .v2-cta { width: 100%; margin-top: 12px; border: 0; border-radius: 999px; background: #f4f7fb; color: #111; font-size: 15px; font-weight: 700; padding: 12px 18px; display: flex; align-items: center; justify-content: center; gap: 8px; }
        .v2-cta:disabled { opacity: 0.6; }
        @media (min-width: 980px) {
          .v2-root { grid-template-columns: minmax(460px, 44vw) 1fr; grid-template-rows: 1fr; }
          .v2 { grid-row: 1; grid-column: 1; padding: 18px 26px 16px; background: radial-gradient(120% 80% at 0% 0%, #161616 0%, #070707 46%); }
          .v2-photo { grid-row: 1; grid-column: 2; }
          .v2 h1 { font-size: clamp(32px, 4.2vh, 52px); }
          .v2-social { display: flex; align-items: center; gap: 10px; margin-top: 14px; }
          .v2-access { display: block; margin: 8px 0 10px; }
          .v2-perks { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 12px; list-style: none; margin: 0; padding: 0; }
        }
      `}</style>
    </div>
  );
}
